import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Coins, Crown, Zap } from "lucide-react";

export default function Credits() {
    const { t } = useTranslation();
    const { workspace } = useAuth();
    const [packages, setPackages] = useState({});
    const [loadingPkg, setLoadingPkg] = useState(null);

    useEffect(() => {
        api.get("/payments/packages").then((r) => setPackages(r.data));
    }, []);

    const checkout = async (packageId) => {
        setLoadingPkg(packageId);
        try {
            const res = await api.post("/payments/checkout", {
                package_id: packageId,
                origin_url: window.location.origin,
            });
            window.location.href = res.data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("credits.checkout_failed"));
            setLoadingPkg(null);
        }
    };

    const subs = Object.entries(packages).filter(([_, p]) => p.kind === "subscription");
    const tops = Object.entries(packages).filter(([_, p]) => p.kind === "topup");

    return (
        <PageHeader title={t("credits.title")} subtitle={t("credits.subtitle")}>
            <div className="rounded-2xl border border-[var(--jr-brand)]/40 bg-gradient-to-br from-[var(--jr-brand)]/10 to-transparent p-8 mb-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">{t("credits.current_balance")}</div>
                        <div className="mt-2 font-display text-6xl tracking-tighter text-white">{workspace?.credits ?? 0}</div>
                        <div className="mt-1 text-[var(--jr-text-2)]">{t("credits.available")}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">{t("credits.plan")}</div>
                        <div className="mt-2 font-display text-3xl tracking-tight text-[var(--jr-brand)]">{workspace?.tier}</div>
                        <div className="mt-1 text-[var(--jr-text-2)] text-sm">{workspace?.credits_used_this_month} {t("credits.used_this_month")}</div>
                    </div>
                </div>
            </div>

            <h2 className="font-display text-2xl tracking-tight mt-10">{t("credits.subscription_tiers")}</h2>
            <p className="text-sm text-[var(--jr-text-2)] mb-4">{t("credits.upgrade_hint")}</p>
            <div className="grid md:grid-cols-3 gap-4">
                {subs.map(([id, p]) => (
                    <PackageCard key={id} id={id} pkg={p} loading={loadingPkg === id} onBuy={checkout} icon={Crown} accent={workspace?.tier === id ? "current" : "default"}/>
                ))}
            </div>

            <h2 className="font-display text-2xl tracking-tight mt-10">{t("credits.topups")}</h2>
            <p className="text-sm text-[var(--jr-text-2)] mb-4">{t("credits.topups_hint")}</p>
            <div className="grid md:grid-cols-3 gap-4">
                {tops.map(([id, p]) => (
                    <PackageCard key={id} id={id} pkg={p} loading={loadingPkg === id} onBuy={checkout} icon={Zap} accent="default"/>
                ))}
            </div>
        </PageHeader>
    );
}

function PackageCard({ id, pkg, loading, onBuy, icon: Icon, accent }) {
    const { t } = useTranslation();
    const isCurrent = accent === "current";
    return (
        <div data-testid={`pkg-${id}`} className={`rounded-xl border p-6 flex flex-col ${isCurrent ? "border-[var(--jr-brand)] glow-brand bg-[var(--jr-surface)]" : "border-white/8 bg-[var(--jr-surface)]"}`}>
            <div className="flex items-center justify-between">
                <Icon className={isCurrent ? "text-[var(--jr-brand)]" : "text-[var(--jr-text-2)]"} size={20}/>
                {isCurrent && <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-brand)]">{t("credits.active")}</span>}
            </div>
            <div className="mt-5">
                <div className="font-display text-2xl tracking-tight text-white">{pkg.label}</div>
                <div className="mt-3 font-display text-4xl tracking-tighter">€{pkg.amount.toFixed(2)}</div>
                <div className="text-sm text-[var(--jr-text-2)] mt-1 inline-flex items-center gap-1"><Coins size={14}/> +{pkg.credits} credits</div>
            </div>
            <button
                disabled={loading}
                onClick={() => onBuy(id)}
                data-testid={`buy-${id}`}
                className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[var(--jr-brand)] text-white font-medium hover:bg-[var(--jr-brand-2)] transition-colors disabled:opacity-60"
            >
                {loading ? t("credits.redirecting") : isCurrent ? t("credits.renew") : t("credits.buy")}
            </button>
        </div>
    );
}
